import { Card, Checkbox, Grid, InputLabel, Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDSelect from "components/MDSelect";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { useUser } from "context/UserContext";
import DataTable from "examples/Tables/DataTable";
import { Form, Formik } from "formik";
import DefaultCell from "layouts/dashboards/sales/components/DefaultCell";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toDateTime } from "utils/DateUtils";
import { errorAlert, successAlert } from "utils/Functions";
import { get, post, postNoResponse } from "utils/WebService";
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Icon } from "@mui/material";
import moment from "moment";
import DeadlinesCreateEdit from "views/legalConsult/cases/tabs/Deadlines/DeadlinesCreateEdit";

export default function BatchRescheduling() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    
    // As Enums / Configurações base do usuário logado
    const { tipoEventoEnum, statusPrazoEnum, loggedUser, userId } = useUser();
    
    const [data, setData] = useState([]);
    const [pages, setPages] = useState(0);
    const [recordsTotal, setRecordsTotal] = useState(0);
    const [loadingTable, setLoadingTable] = useState(false);
    const [teamUsers, setTeamUsers] = useState([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    
    const [selectedEvents, setSelectedEvents] = useState([]);
    const [appliedFilter, setAppliedFilter] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [novaData, setNovaData] = useState("");
    const [motivo, setMotivo] = useState("");
    const [viewEventId, setViewEventId] = useState(null);
    const tableState = useRef({
        pageSize: 10,
        pageIndex: 0,
        sorting: [],
        dataInicial: "",
        dataFinal: "",
        usuarioId: null,
        tipoEventoId: null
    });

    useEffect(() => {
        if (!userId) return;
        setIsLoadingUsers(true);
        // Regra de Membros da Equipe do atual gestor/coordenador logado
        get(`/equipe/usuarios/${userId}`)
            .then(res => {
                const data = Array.isArray(res) ? res : (res.list || []);
                if (data.length === 0) {
                    get("/users/list-all").then(res2 => {
                        let fallback = Array.isArray(res2) ? res2 : (res2.list || []);
                        setTeamUsers(fallback.filter(u => u.perfils?.some(p => p.nome.toLowerCase().includes("membro"))));
                        setIsLoadingUsers(false);
                    }).catch(() => {
                        setTeamUsers([]);
                        setIsLoadingUsers(false);
                    });
                } else {
                    setTeamUsers(data);
                    setIsLoadingUsers(false);
                }
            })
            .catch(() => {
                setTeamUsers([]);
                setIsLoadingUsers(false);
            });
    }, [userId]);

    const fetchData = useCallback(({ pageSize, pageIndex, sorting, dataInicial, dataFinal, usuarioId, tipoEventoId }) => {
        setLoadingTable(true);

        const updatedTableState = {
            pageSize: pageSize ?? tableState.current.pageSize,
            pageIndex: pageIndex ?? tableState.current.pageIndex,
            sorting: sorting ?? tableState.current.sorting,
            dataInicial,
            dataFinal,
            usuarioId,
            tipoEventoId
        };
        tableState.current = updatedTableState;

        const defaultTeamIds = [...new Set([...teamUsers.map(u => u.id), userId])];

        const payload = {
            page: updatedTableState.pageIndex,
            pageSize: updatedTableState.pageSize,
            sorted: updatedTableState.sorting,
            filtered: [],
            dataInicio: updatedTableState.dataInicial || null,
            dataFim: updatedTableState.dataFinal ? moment(updatedTableState.dataFinal, 'YYYY-MM-DD').add(1, 'days').format('YYYY-MM-DD') : null,
            tipoPrazo: updatedTableState.tipoEventoId || null,
            pessoaId: updatedTableState.usuarioId || null,
            pessoasEquipeIds: !updatedTableState.usuarioId ? defaultTeamIds : null,
            statusPrazo: 1 // Pending/Aberto limits
        };

        post("/prazo/list", payload)
            .then(({ data, pages, recordsTotal }) => {
                setData(data || []);
                setPages(pages || 0);
                setRecordsTotal(recordsTotal || 0);
            })
            .catch(() => {
                setData([]);
            }).finally(() => {
                setLoadingTable(false);
            });

    }, [teamUsers, userId]);

    const handleSelectRow = (rowId) => {
        setSelectedEvents(prev => 
            prev.includes(rowId) ? prev.filter(id => id !== rowId) : [...prev, rowId]
        );
    };

    const getBlockedReason = (r) => {
        const prazoCode = typeof r.tipoPrazo === 'object' ? r.tipoPrazo?.code : r.tipoPrazo;
        if (String(prazoCode).toLowerCase() === 'peremptory') return "Não é possível reagendar esse tipo de Evento";
        
        const maxReschedulesMap = {
            audience: 3,
            peremptory: 0,
            simple: 5,
            measures: 5,
            endSuspensionProcess: 3,
            endSuspension: 3
        };

        const max = maxReschedulesMap[prazoCode] !== undefined ? maxReschedulesMap[prazoCode] : 0;
        const current = r.qtdReagendamento || 0;
        
        if (current >= max && max !== 0) return `Máximo de Reagendamento Atingido (${current}/${max})`;
        return null;
    };

    const isBlocked = (r) => getBlockedReason(r) !== null;

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            // Check all, mas pular preemptórios e já esgotados
            const permitidos = data.filter(r => !isBlocked(r)).map(r => r.id);
            setSelectedEvents(permitidos);
        } else {
            setSelectedEvents([]);
        }
    };

    // Colunas da tabela
    const userColumns = [
        {
            id: 'select',
            isSorted: false,
            header: (
                <Checkbox
                    checked={data.length > 0 && selectedEvents.length === data.filter(r => !isBlocked(r)).length}
                    indeterminate={selectedEvents.length > 0 && selectedEvents.length < data.filter(r => !isBlocked(r)).length}
                    onChange={handleSelectAll}
                    sx={{ 
                        padding: "0 !important", 
                        opacity: "1 !important",
                        color: darkMode ? "#fff" : "rgba(0, 0, 0, 0.6)"
                    }}
                />
            ),
            cell: ({ row }) => {
                const blockedReason = getBlockedReason(row.original);
                const disabled = blockedReason !== null;
                return (
                    <Tooltip title={blockedReason || ""} placement="right">
                        <span>
                            <Checkbox
                                disabled={disabled}
                                checked={selectedEvents.includes(row.original.id)}
                                onChange={() => handleSelectRow(row.original.id)}
                                sx={disabled ? { 
                                    color: darkMode ? "#7b809a" : "#ced4da", 
                                    "&.Mui-disabled": { 
                                        color: darkMode ? "#7b809a" : "#ced4da", 
                                        opacity: 1 
                                    } 
                                } : {}}
                            />
                        </span>
                    </Tooltip>
                );
            },
            width: "5%",
            isSorted: false
        },
        {
            header: "Ação",
            accessorKey: "action",
            cell: info => (
                <Tooltip title="Visualizar Detalhes">
                    <IconButton onClick={(e) => { e.stopPropagation(); setViewEventId(info.row.original.id); }}>
                        <Icon color="info">visibility</Icon>
                    </IconButton>
                </Tooltip>
            ),
            isSorted: false
        },
        {
            header: "Agendamento",
            accessorKey: "tipoPrazo",
            cell: info => {
                const tipo = tipoEventoEnum?.find(ht => ht.code === info.getValue());
                return <DefaultCell>{tipo?.name || info.getValue() || "N/A"}</DefaultCell>;
            },
            isSorted: true
        },
        {
            header: "Status",
            accessorKey: "statusPrazo",
            cell: info => {
                const status = statusPrazoEnum?.find(ht => ht.code === info.getValue());
                return <DefaultCell>{status?.name || info.getValue() || "N/A"}</DefaultCell>;
            },
            isSorted: true
        },
        {
            header: "Data Agenda",
            accessorKey: "data",
            cell: info => <DefaultCell>{info.getValue() ? toDateTime(info.getValue()) : "N/A"}</DefaultCell>,
            isSorted: true
        },
        {
            header: "Responsável",
            isSorted: false,
            accessorKey: "pessoa",
            cell: info => <DefaultCell>{info.getValue()?.nome || "N/A"}</DefaultCell>
        }
    ];

    const confirmReschedule = () => {
        if (selectedEvents.length === 0) return;
        setOpenDialog(true);
    };

    const processBatch = () => {
        if (!novaData) {
            errorAlert("Informe a nova data para o reagendamento!");
            return;
        }

        const payload = {
            eventIds: selectedEvents,
            novaData: novaData,
            motivo: motivo
        };

        setLoadingTable(true);
        setOpenDialog(false); // Fecha dialog antes pra não esconder o Swal de Erro/Sucesso
        
        postNoResponse("/prazo/reagendamento-lote/process", payload)
            .then(() => {
                successAlert("Eventos selecionados reagendados com sucesso!");
                setNovaData("");
                setMotivo("");
                setSelectedEvents([]);
                fetchData({}); // Refresh list
            })
            .catch((e) => {
                errorAlert(e.message || "Erro ao reagendar os itens.");
            })
            .finally(() => {
                setLoadingTable(false);
            });
    };

    return (
        <Grid container display="flex" justifyContent="center">
            <Grid item xs={12} md={12}>
                <Card
                    variant="outlined"
                    sx={{
                        px: 2,
                        py: 1.5,
                        mb: 2,
                        background: darkMode ? "#23233a" : "#fafafa",
                        borderColor: darkMode ? "#333" : "#ddd",
                    }}
                >
                    <Grid container spacing={1}>
                        <Formik
                            initialValues={{
                                dataInicial: '',
                                dataFinal: '',
                                usuarioId: null,
                                tipoEventoId: null,
                            }}
                            onSubmit={(values) => {
                                setAppliedFilter(true);
                                setSelectedEvents([]); // reset selections on new fetch
                                fetchData({
                                    pageSize: tableState.current.pageSize,
                                    pageIndex: 0,
                                    dataInicial: values.dataInicial,
                                    dataFinal: values.dataFinal,
                                    usuarioId: values.usuarioId?.id || null,
                                    tipoEventoId: values.tipoEventoId?.id || null,
                                });
                            }}
                        >
                            {({ values, setFieldValue, resetForm }) => (
                                <Form style={{ width: '100%' }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={3}>
                                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Data Início</InputLabel>
                                            <MDInput
                                                type="date"
                                                size="small"
                                                fullWidth
                                                value={values.dataInicial}
                                                onChange={(e) => setFieldValue('dataInicial', e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={3}>
                                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Data Fim</InputLabel>
                                            <MDInput
                                                type="date"
                                                size="small"
                                                fullWidth
                                                value={values.dataFinal}
                                                onChange={(e) => setFieldValue('dataFinal', e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={3}>
                                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Responsável</InputLabel>
                                            <MDSelect
                                                name='usuarioId'
                                                isLoading={isLoadingUsers}
                                                getOptionValue={(option) => option.id}
                                                getOptionLabel={(option) => option.nome}
                                                placeholder="Equipe..."
                                                value={[{id: null, nome: "Todos"}, ...teamUsers].find((item) => item.id === values.usuarioId?.id) || null}
                                                options={[{id: null, nome: "Todos"}, ...teamUsers]}
                                                onChange={(val) => setFieldValue('usuarioId', val || null)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={3}>
                                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Tipo Evento</InputLabel>
                                            <MDSelect
                                                name="tipoEventoId"
                                                getOptionValue={(option) => option.id}
                                                getOptionLabel={(option) => option.name}
                                                value={[{id: null, name: "Todos"}, ...(tipoEventoEnum || [])].find((item) => item.id === values.tipoEventoId?.id) || null}
                                                placeholder="Selecione um tipo"
                                                options={[{id: null, name: "Todos"}, ...(tipoEventoEnum || [])]}
                                                onChange={(val) => setFieldValue('tipoEventoId', val || null)}
                                            />
                                        </Grid>

                                        <Grid item xs={12} display="flex" justifyContent="flex-end" alignItems="center">
                                            <MDButton
                                                variant="contained"
                                                color="info"
                                                sx={{ mr: 2 }}
                                                type="button"
                                                onClick={() => {
                                                    resetForm();
                                                    setAppliedFilter(false);
                                                    setData([]);
                                                }}
                                            >
                                                Limpar filtro
                                            </MDButton>
                                            <MDButton
                                                variant="contained"
                                                color="info"
                                                type="submit"
                                            >
                                                Pesquisar Eventos
                                            </MDButton>
                                        </Grid>
                                    </Grid>
                                </Form>
                            )}
                        </Formik>
                    </Grid>
                </Card>

                {appliedFilter && (
                    <MDBox mt={2}>
                        <MDBox mb={2} display="flex" justifyContent="flex-end">
                            <MDButton 
                                variant="gradient" 
                                color="warning" 
                                disabled={selectedEvents.length === 0}
                                onClick={confirmReschedule}
                            >
                                Processar Reagendamentos ({selectedEvents.length})
                            </MDButton>
                        </MDBox>
                        <DataTable
                            loading={loadingTable}
                            columnData={userColumns}
                            data={data}
                            fetchData={fetchData}
                            totalPages={pages}
                            totalRecords={recordsTotal}
                            tableState={tableState}
                            disableSelectByClick={true} // prevent firing row clicks for checkbox
                        />
                    </MDBox>
                )}

                <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm"
                    PaperProps={{
                        sx: {
                            backgroundColor: darkMode ? "#1a2035" : "#fff",
                            color: darkMode ? "#fff" : "#344767",
                            borderRadius: 2
                        }
                    }}
                >
                    <DialogTitle>Confirmar Reagendamento em Lote ({selectedEvents.length} itens)</DialogTitle>
                    <DialogContent dividers>
                        <MDTypography variant="body2" color="text" mb={2}>
                            Escreva o motivo e selecione a nova data alvo para aplicar nos eventos selecionados.
                        </MDTypography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Nova Data</InputLabel>
                                <MDInput
                                    type="date"
                                    size="small"
                                    fullWidth
                                    value={novaData}
                                    onChange={(e) => setNovaData(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Motivo</InputLabel>
                                <MDInput
                                    type="text"
                                    size="small"
                                    fullWidth
                                    value={motivo}
                                    onChange={(e) => setMotivo(e.target.value)}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <MDButton onClick={() => setOpenDialog(false)} color="dark" variant="outlined">
                            Cancelar
                        </MDButton>
                        <MDButton onClick={processBatch} color="info" variant="gradient">
                            Confirmar
                        </MDButton>
                    </DialogActions>
                </Dialog>
            </Grid>

            <Dialog
                open={Boolean(viewEventId)}
                onClose={() => setViewEventId(null)}
                maxWidth="md"
                fullWidth
            >
                <DialogContent sx={{ p: 0 }}>
                    {viewEventId && (
                        <DeadlinesCreateEdit
                            id={viewEventId}
                            closeCreateEdit={() => setViewEventId(null)}
                            refreshTable={() => { setViewEventId(null); fetchData({}); }}
                            casoProcessualId={null}
                        />
                    )}
                </DialogContent>
            </Dialog>

        </Grid>
    );
}
